import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const COOKIE_CONSENT_KEY = 'gc-imoveis-cookie-consent';

const privacyPolicyContent = `
## Política de Privacidade

**Última atualização: Janeiro de 2025**

### 1. Informações que Coletamos

A GC Imóveis coleta informações que você nos fornece diretamente, como:
- Nome completo
- Endereço de e-mail
- Número de telefone/WhatsApp
- Mensagens enviadas através do formulário de contato

### 2. Como Usamos Suas Informações

Utilizamos as informações coletadas para:
- Responder às suas solicitações e dúvidas
- Enviar informações sobre imóveis de seu interesse
- Melhorar nossos serviços e experiência do usuário
- Cumprir obrigações legais

### 3. Cookies

Nosso site utiliza cookies para:
- Lembrar suas preferências
- Analisar o tráfego do site
- Melhorar a navegação

Você pode controlar o uso de cookies através das configurações do seu navegador.

### 4. Compartilhamento de Dados

Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros, exceto:
- Quando necessário para prestação de serviços
- Por exigência legal

### 5. Segurança

Implementamos medidas de segurança para proteger suas informações contra acesso não autorizado, alteração ou destruição.

### 6. Seus Direitos

Você tem direito a:
- Acessar seus dados pessoais
- Corrigir dados incorretos
- Solicitar a exclusão de seus dados
- Revogar o consentimento

### 7. Contato

Para questões sobre privacidade, entre em contato:
- E-mail: guilherme@iabadia.com.br
- WhatsApp: (19) 97171-0050

### 8. Alterações

Esta política pode ser atualizada periodicamente. Recomendamos verificar regularmente.
`;

const termsOfUseContent = `
## Termos de Uso

**Última atualização: Janeiro de 2025**

### 1. Aceitação dos Termos

Ao acessar e usar o site GC Imóveis, você concorda com estes Termos de Uso. Se não concordar, por favor, não utilize o site.

### 2. Descrição do Serviço

O site GC Imóveis oferece:
- Listagem de imóveis para venda e locação
- Informações sobre o mercado imobiliário
- Canais de contato com o corretor

### 3. Uso Permitido

Você pode usar o site para:
- Visualizar imóveis disponíveis
- Entrar em contato para mais informações
- Agendar visitas

### 4. Uso Proibido

É proibido:
- Copiar ou reproduzir conteúdo sem autorização
- Usar o site para fins ilegais
- Tentar acessar áreas restritas
- Transmitir vírus ou código malicioso

### 5. Propriedade Intelectual

Todo o conteúdo do site, incluindo textos, imagens, logotipos e design, é propriedade da GC Imóveis e está protegido por leis de direitos autorais.

### 6. Informações dos Imóveis

As informações sobre os imóveis são fornecidas de boa-fé, mas podem estar sujeitas a alterações sem aviso prévio. Recomendamos confirmar todos os detalhes antes de qualquer decisão.

### 7. Limitação de Responsabilidade

A GC Imóveis não se responsabiliza por:
- Erros ou omissões nas informações
- Danos decorrentes do uso do site
- Conteúdo de sites externos linkados

### 8. Links Externos

O site pode conter links para sites de terceiros. Não temos controle sobre esses sites e não nos responsabilizamos por seu conteúdo.

### 9. Modificações

Reservamo-nos o direito de modificar estes termos a qualquer momento. Alterações entram em vigor imediatamente após a publicação.

### 10. Lei Aplicável

Estes termos são regidos pelas leis brasileiras. Qualquer disputa será resolvida nos tribunais de Paulínia/SP.

### 11. Contato

Dúvidas sobre os Termos de Uso:
- E-mail: guilherme@iabadia.com.br
- WhatsApp: (19) 97171-0050
`;

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setIsVisible(false);
  };

  const handleOptOut = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#2a2a2a] py-3 px-4 shadow-lg">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 text-center sm:text-left">
            <p className="text-white text-sm">
              Nós utilizamos cookies para garantir que você tenha a melhor experiência em nosso site.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-primary underline hover:text-primary/80 font-medium"
              >
                Política de Privacidade
              </button>
              <span className="text-gray-400">-</span>
              <button
                onClick={() => setShowTerms(true)}
                className="text-primary underline hover:text-primary/80 font-medium"
              >
                Termos de uso
              </button>
            </div>
          </div>
          <Button
            onClick={handleAccept}
            variant="outline"
            className="bg-transparent border-white text-white hover:bg-white hover:text-[#2a2a2a] text-xs font-semibold px-6 py-2 h-auto whitespace-nowrap"
          >
            CONCORDO
          </Button>
        </div>
      </div>

      {/* Privacy Policy Dialog */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Política de Privacidade</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="prose prose-sm max-w-none">
              {privacyPolicyContent.split('\n').map((line, index) => {
                if (line.startsWith('## ')) {
                  return null; // Skip main title as it's in DialogTitle
                }
                if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-lg font-semibold mt-4 mb-2">{line.replace('### ', '')}</h3>;
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="text-gray-500 text-sm mb-4">{line.replace(/\*\*/g, '')}</p>;
                }
                if (line.startsWith('- ')) {
                  return <li key={index} className="ml-4 text-gray-700">{line.replace('- ', '')}</li>;
                }
                if (line.trim()) {
                  return <p key={index} className="text-gray-700 mb-2">{line}</p>;
                }
                return null;
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Terms of Use Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Termos de Uso</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="prose prose-sm max-w-none">
              {termsOfUseContent.split('\n').map((line, index) => {
                if (line.startsWith('## ')) {
                  return null; // Skip main title as it's in DialogTitle
                }
                if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-lg font-semibold mt-4 mb-2">{line.replace('### ', '')}</h3>;
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={index} className="text-gray-500 text-sm mb-4">{line.replace(/\*\*/g, '')}</p>;
                }
                if (line.startsWith('- ')) {
                  return <li key={index} className="ml-4 text-gray-700">{line.replace('- ', '')}</li>;
                }
                if (line.trim()) {
                  return <p key={index} className="text-gray-700 mb-2">{line}</p>;
                }
                return null;
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
